import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShieldAlert, Loader2, RefreshCw, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = '/api';

const Stat = ({ label, value, tone }) => (
  <div className={`rounded-lg border px-3 py-2 ${tone || 'border-slate-200 bg-white'}`}>
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-lg font-bold text-slate-900">{value}</p>
  </div>
);

const Section = ({ title, hint, rows, cols, testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={testId}>
    <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
      <span className="text-sm font-semibold text-slate-900">{title}</span>
      <span className="text-xs text-slate-500">— {hint}</span>
      <span className="ml-auto text-xs font-semibold text-slate-700">{rows.length}</span>
    </div>
    {rows.length === 0 ? (
      <p className="px-3 py-3 text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Nothing affected.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">{cols.map(c => <th key={c.k} className="px-3 py-1.5 font-semibold">{c.h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 align-top">
                {cols.map(c => <td key={c.k} className="px-3 py-1.5 text-slate-700">{c.render ? c.render(r) : (r[c.k] || '—')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default function ExclusionAuditPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/audit/exclusions`, { withCredentials: true }); setData(r.data); } catch { setData({ error: true }); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const assetLink = r => <Link to={`/biotech/${r.biotech_company_id}`} className="text-sky-700 hover:underline font-medium">{r.asset_name}</Link>;
  const pharmaLink = r => (r.pharma_profile_id ? <Link to={`/company-profile/${r.pharma_profile_id}`} className="text-sky-700 hover:underline">{r.pharma}</Link> : r.pharma);
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="exclusion-audit-page">
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-5 h-5 text-amber-600" />
        <h1 className="text-lg font-bold text-slate-900">Exclusion Audit</h1>
        <span className="text-xs text-slate-500">Re-checks every past asset → pharma pairing against the corrected exclusion rules (Takeda × HB0056 fix)</span>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="ml-auto text-xs gap-1" data-testid="audit-refresh">{loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Re-run</Button>
      </div>
      {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      {data?.error && <p className="text-sm text-rose-700">Audit failed to load.</p>}
      {data && !data.error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat label="Assets checked" value={data.assets_checked} />
            <Stat label="Asset × pharma pairs" value={data.pairs_checked} />
            <Stat label="Recipients wrongly hidden" value={data.summary.hidden_recipients} tone={data.summary.hidden_recipients ? 'border-amber-300 bg-amber-50' : ''} />
            <Stat label="Companies wrongly blocked" value={data.summary.unblocked_companies} tone={data.summary.unblocked_companies ? 'border-rose-300 bg-rose-50' : ''} />
            <Stat label="Co-dev partners parked" value={data.summary.codev_parked} tone={data.summary.codev_parked ? 'border-amber-300 bg-amber-50' : ''} />
          </div>
          <p className="text-xs text-slate-500">Rows below are pairings whose verdict changed with the fix — each is now reachable / kept in outreach. Re-run <b>Refresh Matches</b> on the asset to regenerate its list. Hard blocks that remain valid: {data.summary.hard_blocks_still_valid}.</p>
          <Section testId="audit-hidden" title="Recipients wrongly hidden" hint="a contact's personal exclusion matched only by substring or loose synonym (e.g. 'RA' ⊂ respiratory, IBS ≈ IBD)"
            rows={data.hidden_recipients} cols={[{ k: 'asset_name', h: 'Asset', render: assetLink }, { k: 'pharma', h: 'Pharma', render: pharmaLink }, { k: 'contact_name', h: 'Contact', render: r => <>{r.contact_name}<div className="text-[10px] text-slate-400">{r.contact_email}</div></> }, { k: 'exclusion', h: 'Their exclusion' }, { k: 'asset_ta', h: 'Asset TA / indications', render: r => <>{r.asset_ta}<div className="text-[10px] text-slate-400">{r.asset_indication}</div></> }, { k: 'mapped_at', h: 'Mapped' }]} />
          <Section testId="audit-unblocked" title="Companies wrongly blocked" hint="an absolute signal block recorded on the mapping that no longer holds under the corrected rules"
            rows={data.unblocked_companies} cols={[{ k: 'asset_name', h: 'Asset', render: assetLink }, { k: 'pharma', h: 'Pharma', render: pharmaLink }, { k: 'old_reason', h: 'Recorded reason' }, { k: 'mapped_at', h: 'Mapped' }]} />
          <Section testId="audit-codev" title="Co-development partners parked" hint="asset seeks co-development/JV but the partner was auto-moved to the exploratory bucket"
            rows={data.codev_parked} cols={[{ k: 'asset_name', h: 'Asset', render: assetLink }, { k: 'pharma', h: 'Pharma', render: pharmaLink }, { k: 'bucket', h: 'Bucket' }, { k: 'mapped_at', h: 'Mapped' }]} />
        </>
      )}
    </div>
  );
}
